/**
 * useResendCountdown — the "wait N seconds before sending another code" countdown.
 *
 * Every surface that emails or texts a one-time code offers "Resend", and every server behind one
 * refuses a resend inside a cooldown (`@puffin-core/auth-routes`' password reset: 60 s,
 * `resendCooldownMs`; the MFA OTP send: the same window). A resend button that is live during that
 * window invites a refusal the user could not have predicted, so the countdown is shown instead.
 *
 * Promoted from `useOtpSendFlow`'s module-private copy (UNREACHABLE-MODULE-BURNDOWN-001 L16), when
 * `@puffin-core/login-shell`'s password-reset modal needed the same countdown — a second copy is
 * how the fleet's three `useRateLimit`s diverged (Q-UMB-8). One addition came with the promotion:
 * `start(seconds)` takes an OVERRIDE, because the server is the authority on its own cooldown — a
 * refusal carries `retryAfter`, and the countdown must resynchronise to it rather than restart at
 * the client's default.
 *
 * No `isMounted` ceremony — React 19 silently no-ops setState on an unmounted component (see
 * GUIDES/shared/standards/testing/react-19-unmount-semantics.md). The unmount cleanup clears the
 * interval — real cleanup work the `web/require-effect-cleanup` rule enforces.
 *
 * @module @puffin-core/mfa-ui/hooks/useResendCountdown
 */

import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * @param {number} cooldown - the default cooldown, in whole seconds
 * @returns {{
 *   secondsRemaining: number,
 *   start: (seconds?: number) => void,
 *   stop: () => void,
 * }} `start()` begins (or restarts) the countdown at `cooldown`, or at `seconds` when the server
 *   named its own; `stop()` zeroes it and releases the interval.
 */
export function useResendCountdown(cooldown) {
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const start = useCallback(
    (seconds = cooldown) => {
      setSecondsRemaining(seconds);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [cooldown],
  );

  const stop = useCallback(() => {
    setSecondsRemaining(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return { secondsRemaining, start, stop };
}

export default useResendCountdown;
